import { WithActionBar } from "@/src/components/wrappers/WithActionBar";
import { Content } from "@/src/components/wrappers/Content";
import { Steps } from "@/src/components/ui/steps";
import { useFetch } from "@/src/lib/useFetch";
import { useToast } from "@/src/components/ui/use-toast";
import { useLoaderData, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { fetch } from "@/src/lib/fetch";
import { useStepsStore, BUILDER_STEPS, BUILDER_STEP_LABELS } from "./EditOffer/state";
import { Offer } from "./model";
import { OfferForm } from "./EditOffer/offer.schema";
import { EditOfferForm } from "./EditOffer/EditOfferForm";
import { OfferActionBar } from "./OfferActionBar";
import { useEffect } from "react";
import { useOffersStore } from "../../stores/offersStore";
import { useOfferData } from "@/src/hooks/useOfferData";
import { Loader, parseDateIntoTime } from "./EditOffer/ChangeYachtDetailsModal/ChangeYachtDetailsModal";
import { logOfferCreatorAction } from "./offerCreatorLogs";

export function EditOffer() {
  const offerLoaded = useLoaderData() as Offer;
  const location = useLocation();
  const offerId = location.pathname.split("/").pop() ?? "";

  const { offerData, usersData, yachtsData, feedbacksData, isLoadingYachts, isLoading } = useOfferData(offerId);
  const offer = { ...offerData, ...usersData, ...yachtsData, ...feedbacksData } as Offer;

  const steps = useStepsStore();
  const { fetch, isFetching } = useFetch();
  const { toast } = useToast();
  const { setOfferYachts } = useOffersStore();
  const navigate = useNavigate();
  const [params, _] = useSearchParams();

  useEffect(() => {
    setOfferYachts(
      offer.yachts?.map((y) => ({
        ...y,
        ignoreAvailability: y.ignoreAvailability ?? false,
        startTime: parseDateIntoTime(y.startDate),
        endTime: parseDateIntoTime(y.endDate),
        pricing: {
          ...y.pricing,
          discountPercentage: y.pricing.discountPercentage ?? 0,
        },
      })) || [],
    );

    const step = params.get("step") as typeof steps.step;
    if (step) {
      steps.setStep(step);
    } else {
      // Editing an existing offer starts on the choose step — the client is
      // already set (the Customer value now opens a dialog, not a step).
      steps.setStep("Yachts");
    }

    if (!isLoadingYachts && offer.id) {
      logOfferCreatorAction({
        eventType: "offer.edit_loaded",
        offerId: offer.id,
        meta: {
          offer_id: offer.id,
          status: offer.status,
          hubspot_id: offer.hubspotId,
          selected_yacht_count: offer.yachts?.length ?? 0,
        },
      });
    }
  }, [isLoadingYachts]);

  const parseDateAndTime = (date: string, time: string) => {
    const [hours, minutes] = time.split(":").map((num) => parseInt(num, 10));
    const dt = new Date(date);
    dt.setUTCHours(hours);
    dt.setUTCMinutes(minutes);
    return dt.toISOString();
  };

  const onSubmit = async (data: OfferForm, emails: string[]) => {
    if (!isFetching) {
      try {
        logOfferCreatorAction({
          eventType: offer.status === "draft" ? "offer.save_intent" : "offer.update_intent",
          offerId: offer.id,
          meta: {
            offer_id: offer.id,
            status_before: offer.status,
            status_after: "saved",
            selected_yacht_count: data.yachts?.length ?? 0,
            email_recipient_count: emails.length,
          },
        });
        const response = await fetch<Offer>("/api/offers/" + offer.id, "PUT", {
          title: data?.description?.title,
          intro: data?.description?.intro,
          brand: data?.description?.brand,
          currency: data?.description?.currency,
          link: "",
          dealLink: "",
          charterStartDate: offer?.charterStartDate || null,
          charterEndDate: offer?.charterEndDate || null,
          charterType: data?.description?.charterType || "skippered",
          status: "saved",
          customer: data?.customer,
          broker: data?.description?.broker?.id,
          guests: data?.description?.guests,
          hubspotId: data?.hubspotId,
          createDeal: data?.customer.createDeal,
          yachts: data?.yachts?.map((yt) => ({
            startLocation: yt.startLocation.id,
            endLocation: yt.endLocation.id,
            yacht: yt.yacht.id,
            paymentSteps: yt.paymentSteps,
            yachtOnHold: yt.yachtOnHold ?? false,
            ignoreAvailability: yt.ignoreAvailability ?? false,
            holdExpiresAt: yt.holdExpiresAt ?? null,
            brokerNote: yt.brokerNote ?? null,
            startDate: parseDateAndTime(yt.startDate, yt.startTime),
            endDate: parseDateAndTime(yt.endDate, yt.endTime),
            discountCharterPrice: yt.discountCharterPrice ?? 0,
            cardPayments: yt.cardPayments,
            amenitiesDetails: yt.amenitiesDetails,
            pricing: yt.pricing,
            order: yt.order,
            id: yt.id,
          })),
          id: offer.id,
          customAmenities: data?.yachts?.map((yt) => yt.customAmenities ?? [])?.flat(),
          mailingSettings: data?.mailing,
        });

        if (offer.status === "draft") {
          await fetch(`/api/offers/${response.id}/email`, "POST", emails);
          toast({
            title: "Offer sent",
            description: "The offer has been sent",
            variant: "default",
          });
        } else {
          toast({
            title: "Offer updated",
            description: "The offer has been updated",
            variant: "default",
          });
        }
        return response.id;
      } catch (e: any) {
        console.error(e);
        toast({
          title: "Failed to save yacht",
          description: e.message || "An error occurred while saving the yacht",
          variant: "destructive",
        });
      }
    }
  };

  const onDraft = async (data: OfferForm) => {
    if (!isFetching) {
      try {
        logOfferCreatorAction({
          eventType: "offer.save_draft_intent",
          offerId: offer.id,
          meta: {
            offer_id: offer.id,
            status_before: offer.status,
            status_after: "draft",
            selected_yacht_count: data.yachts?.length ?? 0,
          },
        });
        await fetch(`/api/offers/${offer.id}`, "PUT", {
          title: data?.description?.title,
          intro: data?.description?.intro,
          brand: data?.description?.brand,
          currency: data?.description?.currency,
          link: "",
          dealLink: "",
          charterStartDate: offer?.charterStartDate || null,
          charterEndDate: offer?.charterEndDate || null,
          charterType: data?.description?.charterType || "skippered",
          status: "draft",
          customer: data?.customer,
          hubspotId: data?.hubspotId,
          broker: data?.description?.broker?.id,
          createDeal: data?.customer.createDeal,
          guests: data?.description?.guests,
          yachts: data?.yachts?.map((yt) => ({
            startLocation: yt.startLocation.id,
            endLocation: yt.endLocation.id,
            yacht: yt.yacht.id,
            paymentSteps: yt.paymentSteps,
            yachtOnHold: yt.yachtOnHold ?? false,
            ignoreAvailability: yt.ignoreAvailability ?? false,
            holdExpiresAt: yt.holdExpiresAt ?? null,
            brokerNote: yt.brokerNote ?? null,
            startDate: parseDateAndTime(yt.startDate, yt.startTime),
            endDate: parseDateAndTime(yt.endDate, yt.endTime),
            discountCharterPrice: yt.discountCharterPrice ?? 0,
            cardPayments: true,
            amenitiesDetails: yt.amenitiesDetails,
            pricing: yt.pricing,
            id: yt.id,
          })),
          id: offer.id,
          customAmenities: data?.yachts?.map((yt) => yt.customAmenities ?? [])?.flat(),
          mailingSettings: data?.mailing,
        });
        toast({
          title: "Offer saved",
          description: "The offer has been drafted",
          variant: "default",
        });
        navigate(`/offers`);
      } catch (e: any) {
        console.error(e);
        toast({
          title: "Failed to save draft offer",
          description: e.message || "An error occurred while saving the draft",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <WithActionBar actionBar={<OfferActionBar disableDrafting={offer.status !== "draft"} />}>
      <Content title="Edit offer details">
        <Steps onStepChange={steps.setStep} step={steps.step as (typeof BUILDER_STEPS)[number]} items={BUILDER_STEPS} labels={BUILDER_STEP_LABELS} className="mb-6" />
        {!isLoading && <EditOfferForm onDraft={onDraft} offer={offer} onSubmit={onSubmit} />}
        {isLoading && <Loader text="Fetching data..." />}
      </Content>
    </WithActionBar>
  );
}

export function offerLoader({ params: { id } }: { params: any }) {
  return fetch<{ data: any }>(`/api/offers/${id}`).then((res) => res);
}

export function dealLoader({ params: { id } }: { params: any }) {
  return fetch<{ data: any }>(`/api/offers/deal/${id}`).then((res) => res.data);
}
